import { useState } from 'react';
import { InfoCard, Progress } from '@backstage/core-components';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { Box, Button, Grid, Typography, makeStyles } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useComponentPage } from '../ComponentPageContext';
import type { SchemaType } from './apiSpec';
import { ApiHeaderCard, CapabilitiesCard, CodeSampleCard } from './ApiBlock';
import { useProvidedApis, type ProvidedApi } from './useProvidedApis';

const useStyles = makeStyles(theme => ({
  stack: { display: 'flex', flexDirection: 'column', gap: theme.spacing(3) },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 0),
    borderBottom: '1px solid var(--ui-border)',
    '&:last-child': { borderBottom: 'none' },
    minWidth: 0,
  },
  owner: { fontSize: '0.85rem', color: 'var(--ui-text-muted)' },
  count: {
    marginLeft: theme.spacing(0.5),
    fontSize: '0.85em',
    fontWeight: 400,
    color: 'var(--ui-text-muted)',
  },
  muted: { color: 'var(--ui-text-muted)' },
  type: {
    width: '100%',
    justifyContent: 'space-between',
    textTransform: 'none',
    fontFamily: 'var(--font-mono)',
    fontSize: '0.85rem',
    padding: theme.spacing(0.5, 0),
  },
  fields: { margin: theme.spacing(0, 0, 1, 1), padding: 0, listStyle: 'none' },
  field: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.8rem',
    padding: theme.spacing(0.25, 0),
    overflowWrap: 'anywhere',
  },
}));

/** How many types stand before "Show all". */
const FIRST = 10;

/**
 * Who uses the component — depends on it, or calls an API it provides — with
 * the team that owns each. The same list the facts bar counts
 * (specs/component-dependencies.md AC-1).
 */
const ConsumersCard = () => {
  const classes = useStyles();
  const { catalog } = useComponentPage();
  const inbound = catalog.relations?.inbound ?? [];
  return (
    <InfoCard
      title={
        <span>
          Consumers
          {!catalog.loading && !catalog.error && (
            <span className={classes.count}> {inbound.length}</span>
          )}
        </span>
      }
      titleTypographyProps={{ component: 'h2' }}
    >
      {catalog.loading && <Progress />}
      {!catalog.loading && catalog.error && (
        <Typography role="alert">
          Couldn't load the consumers from the catalog.
        </Typography>
      )}
      {!catalog.loading && !catalog.error && !inbound.length && (
        <Typography className={classes.muted}>None declared</Typography>
      )}
      {inbound.map(e => (
        <div key={e.ref} className={classes.row}>
          <EntityRefLink entityRef={e.ref} disableTooltip>
            {e.title ?? e.name}
          </EntityRefLink>
          {e.owner && (
            <span className={classes.owner}>
              <EntityRefLink
                entityRef={e.owner}
                defaultKind="group"
                disableTooltip
                hideIcon
              />
            </span>
          )}
        </div>
      ))}
    </InfoCard>
  );
};

const SchemaEntry = ({ type }: { type: SchemaType }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  return (
    <li>
      <Button
        className={classes.type}
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
        endIcon={open ? <ExpandMoreIcon /> : <ChevronRightIcon />}
      >
        {type.name}
      </Button>
      {open && (
        <ul className={classes.fields} aria-label={`Fields of ${type.name}`}>
          {type.fields.length ? (
            type.fields.map(f => (
              <li key={f.name} className={classes.field}>
                {f.name}
                {f.required && '*'}: {f.type}
              </li>
            ))
          ) : (
            <li className={classes.field}>{type.shape}</li>
          )}
        </ul>
      )}
    </li>
  );
};

/** The types an API speaks, the first ten until the reader asks for all. */
const SchemaCard = ({ api, named }: { api: ProvidedApi; named: boolean }) => {
  const [all, setAll] = useState(false);
  const types = api.spec?.schemas ?? [];
  if (!types.length) return null;
  const shown = all ? types : types.slice(0, FIRST);
  return (
    <InfoCard
      title={named ? `Schema · ${api.spec?.title ?? api.name}` : 'Schema'}
      subheader={`${types.length} type${types.length === 1 ? '' : 's'}`}
      titleTypographyProps={{ component: 'h2' }}
    >
      <ul
        style={{ margin: 0, padding: 0, listStyle: 'none' }}
        aria-label={`Types of ${api.spec?.title ?? api.name}`}
      >
        {shown.map(t => (
          <SchemaEntry key={t.name} type={t} />
        ))}
      </ul>
      {types.length > FIRST && (
        <Box mt={1}>
          <Button size="small" onClick={() => setAll(v => !v)}>
            {all ? 'Show the first ten' : `Show all ${types.length}`}
          </Button>
        </Box>
      )}
    </InfoCard>
  );
};

/**
 * A component's API tab (specs/api-docs.md): one block per API it provides —
 * what it is, a request to copy, what it can do — and beside them who uses
 * the component and the types each API speaks.
 */
export const ApiContent = () => {
  const classes = useStyles();
  const { loading, error, apis } = useProvidedApis();

  if (loading) return <Progress />;
  if (error) {
    return (
      <InfoCard title="API">
        <Typography role="alert">
          Couldn't load the APIs from the catalog.
        </Typography>
      </InfoCard>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <div className={classes.stack}>
          {apis.map(api => (
            <section
              key={api.ref}
              className={classes.stack}
              role="group"
              aria-label={api.spec?.title ?? api.title ?? api.name}
            >
              <ApiHeaderCard api={api} />
              <CodeSampleCard api={api} />
              <CapabilitiesCard api={api} />
            </section>
          ))}
        </div>
      </Grid>
      <Grid item xs={12} md={4}>
        <div className={classes.stack}>
          <ConsumersCard />
          {apis.map(api => (
            <SchemaCard key={api.ref} api={api} named={apis.length > 1} />
          ))}
        </div>
      </Grid>
    </Grid>
  );
};
